import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';

import { getAlgoliaCredentials } from '../api/algolia';
import { getAvailableLanguages, getTranslations } from '../api/translations';
import { setAuthConfig } from '../store/authSlice';
import { updateDomains, updateConfigApiKeys, updateAppEnv } from '../store/configSlice';
import { useI18nStore } from '../store/hooks';
import { useAppSelector, useAppDispatch } from '../store/store';
import { loadTranslations } from '../utils/i18n/i18n';
import backUpEN from '../utils/i18n/backUpTranslations/en.json';
import { updateAlgoliaConfig } from '../store/algoliaSlice';

export const defaultPhraseLocale = {
  language: 'EN',
  locale: 'en-GB',
  selected: true,
};

export type AppEnv = 'development' | 'production' | 'pre-production';

interface ConfigSecretUrl {
  clientId: string;
  idpUrl: string;
  domainApi: string;
  appEnv: AppEnv;
  DDFSKey: string;
}

export const useAppHook = () => {
  const [appError, setAppError] = useState(false);
  const dispatch = useAppDispatch();
  const userLanguage = useAppSelector(state => state.user.preferredLanguage);
  const token = useAppSelector(state => state.auth.token);
  const { updateAvailableLanguages } = useI18nStore();
  const [isLoadingLang, setIsLoadingLang] = useState(false);

  useEffect(() => {
    fetch(new URL('/config.env.json', location.origin).toString())
      .then(async response => {
        const configResponse: ConfigSecretUrl = await response.json();

        dispatch(
          updateDomains({
            domainApi: configResponse.domainApi,
          })
        );

        dispatch(updateConfigApiKeys({ ddfsKey: configResponse.DDFSKey }));
        dispatch(setAuthConfig({ clientId: configResponse.clientId, idpUrl: configResponse.idpUrl }));
        dispatch(updateAppEnv(configResponse.appEnv));
      })
      .catch(() => {
        setAppError(true);
      });
  }, []);

  useEffect(() => {
    if (!token) return;

    getAlgoliaCredentials()
      .then(({ applicationId, apiKey }) => dispatch(updateAlgoliaConfig({ appId: applicationId, searchKey: apiKey })))
      .catch(() => toast.error('Unable to load the search configuration.'));
  }, [token]);

  useEffect(() => {
    if (userLanguage) {
      setIsLoadingLang(true);
      getAvailableLanguages()
        .then(availableLanguages => {
          updateAvailableLanguages(availableLanguages);
          const locale = availableLanguages.find(language => language.language === userLanguage)?.locale || 'en-GB';
          return getTranslations(locale).then(result => loadTranslations(locale, { ...backUpEN, ...result }));
        })
        .catch(async () => {
          updateAvailableLanguages([defaultPhraseLocale]);
          await loadTranslations(defaultPhraseLocale.locale, { ...backUpEN });
          toast.warning('Unable to load the languages. The application will be in english by default.');
        })
        .finally(() => setIsLoadingLang(false));
    } else {
      setIsLoadingLang(false);
    }
  }, [userLanguage]);

  return { appError, isLoadingLang };
};
